/**
 * Thin HTTP client for the STILLSUN backend.
 *
 * Everything here degrades: the M2 server is built concurrently, so any endpoint may 404
 * or be missing entirely. `apiTry` swallows those into `null` so a hub tab shows an empty
 * state instead of a stack trace, while `api` throws for the flows that must report a
 * reason to the player (login, register, vendor trades).
 */

import type { ItemStack } from '@stillsun/shared';

const defaultBase = (): string => {
  const fromEnv: unknown = import.meta.env.VITE_API_URL;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) return fromEnv.replace(/\/+$/, '');
  // Same-origin in production behind Caddy; the dev server proxies nothing, so fall back
  // to the local game server's HTTP port.
  if (typeof window !== 'undefined' && window.location.port === '5173') {
    return `${window.location.protocol}//${window.location.hostname}:8080`;
  }
  return '';
};

export const API_BASE = defaultBase();

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
  /** True when the endpoint simply is not deployed yet. */
  get missing(): boolean {
    return this.status === 404 || this.status === 501 || this.status === 0;
  }
}

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  token?: string;
  /** Abort after this many ms. */
  timeoutMs?: number;
}

/** Endpoints already known to be absent, so a hub tab does not re-poll a 404 every open. */
const missingPaths = new Set<string>();

export function markAvailable(path: string): void {
  missingPaths.delete(path);
}

export function knownMissing(path: string): boolean {
  return missingPaths.has(path);
}

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const method = opts.method ?? (opts.body === undefined ? 'GET' : 'POST');
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.token !== undefined && opts.token.length > 0) {
    headers.Authorization = `Bearer ${opts.token}`;
  }

  const controller = new AbortController();
  const timeout = window.setTimeout(() => {
    controller.abort();
  }, opts.timeoutMs ?? 8000);

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      signal: controller.signal,
      ...(opts.body === undefined ? {} : { body: JSON.stringify(opts.body) }),
    });
  } catch (err) {
    // Network failure, CORS, or the server is not running at all.
    missingPaths.add(path);
    throw new ApiError(0, err instanceof Error ? err.message : 'network error');
  } finally {
    window.clearTimeout(timeout);
  }

  if (!res.ok) {
    if (res.status === 404 || res.status === 501) missingPaths.add(path);
    let message = `${String(res.status)} ${res.statusText}`;
    try {
      const text = await res.text();
      if (text.length > 0) {
        try {
          const parsed: unknown = JSON.parse(text);
          if (parsed !== null && typeof parsed === 'object') {
            const rec = parsed as Record<string, unknown>;
            const detail = rec.error ?? rec.message;
            if (typeof detail === 'string' && detail.length > 0) message = detail;
          }
        } catch {
          message = text.slice(0, 200);
        }
      }
    } catch {
      /* body already consumed or unreadable */
    }
    throw new ApiError(res.status, message);
  }

  missingPaths.delete(path);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (text.length === 0) return undefined as T;
  return JSON.parse(text) as T;
}

/** `api` for read paths: any failure becomes `null` and is logged once. */
export async function apiTry<T>(path: string, opts: ApiOptions = {}): Promise<T | null> {
  try {
    return await api<T>(path, opts);
  } catch (err) {
    if (err instanceof ApiError && err.missing) {
      console.info(`[stillsun] ${path} unavailable (${String(err.status)}) — showing empty state`);
    } else {
      console.warn(`[stillsun] ${path} failed`, err);
    }
    return null;
  }
}

// --------------------------------------------------------------------- shapes

/** `role` is 'player' | 'founder' | 'admin' | 'owner'. */
export interface PlayerProfile {
  id: number;
  name: string;
  role: string;
  wick: number;
  level: number;
  /** Present on /api/me for full accounts. */
  kind?: string;
  email?: string;
}
export interface AuthResponse {
  token: string;
  player: PlayerProfile;
}
export interface StashResponse {
  items: ItemStack[];
}
export interface VendorOffer {
  itemId: string;
  price: number;
}
export interface VendorResponse {
  sells: VendorOffer[];
  buys: boolean;
}
export interface ContractEntry {
  id: string | number;
  title?: string;
  name?: string;
  desc?: string;
  description?: string;
  progress?: number;
  goal?: number;
  target?: number;
  reward?: number;
  rewardWick?: number;
  done?: boolean;
  expiresAt?: number;
}
export interface RoadmapEntry {
  id: string | number;
  title?: string;
  text?: string;
  body?: string;
  votes?: number;
  voted?: boolean;
  status?: string;
  category?: string;
}
export interface ChangelogEntry {
  id: string | number;
  ts?: number;
  title?: string;
  body?: string;
  feedbackIds?: (string | number)[];
}
export interface InboxMessage {
  id: string | number;
  ts?: number;
  title?: string;
  subject?: string;
  body?: string;
  text?: string;
  read?: boolean;
}

/**
 * The list endpoints are documented only by name, so accept either a bare array or the
 * obvious `{items|contracts|entries|messages: [...]}` wrapper rather than rendering
 * nothing when the server agent picked the other one.
 */
export function unwrapList<T>(payload: unknown, ...keys: string[]): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (payload !== null && typeof payload === 'object') {
    const rec = payload as Record<string, unknown>;
    for (const key of [...keys, 'items', 'entries', 'results', 'data']) {
      const v = rec[key];
      if (Array.isArray(v)) return v as T[];
    }
  }
  return [];
}
